import React, { useEffect, useState } from 'react';
import { useDistinctValuesQuery } from 'src/api/queries/deliveryAnalyticsQueries';
import {
  DeliveryAnalyticsFilters,
  DistinctDimension,
} from 'src/types/TDeliveryAnalytics';
import {
  RangeKey,
  RANGE_LABELS,
} from '../useDeliveryFilters';
import styles from '../styles.module.scss';

type AttrKey =
  | 'campaign_key'
  | 'channel_key'
  | 'placement_key'
  | 'variant_key'
  | 'audience_key';

const ATTR_FIELDS: { key: AttrKey; dimension: DistinctDimension; label: string }[] = [
  { key: 'campaign_key', dimension: 'campaign_key', label: 'Campaign' },
  { key: 'channel_key', dimension: 'channel_key', label: 'Channel' },
  { key: 'placement_key', dimension: 'placement_key', label: 'Placement' },
  { key: 'variant_key', dimension: 'variant_key', label: 'Variant' },
  { key: 'audience_key', dimension: 'audience_key', label: 'Audience' },
];

interface Props {
  range: RangeKey;
  attrs: Record<AttrKey, string>;
  /** Range-only filters (no attrs) used to populate autocomplete suggestions. */
  rangeFilters: DeliveryAnalyticsFilters;
  onRangeChange: (r: RangeKey) => void;
  /** Commit the whole attr draft at once (Apply button / Enter). */
  onApply: (attrs: Record<AttrKey, string>) => void;
  onClear: () => void;
  /** Read-only mode — the backend feature isn't available yet. */
  disabled?: boolean;
}

const RANGES: RangeKey[] = ['24h', '7d', '30d', '90d'];

const ATTR_KEYS = ATTR_FIELDS.map((f) => f.key);

/** Controlled, presentational attr input. The draft lives in the parent so all
 *  fields are committed together by Apply; this input never commits on its own. */
const AttrInput: React.FC<{
  field: { key: AttrKey; dimension: DistinctDimension; label: string };
  value: string;
  rangeFilters: DeliveryAnalyticsFilters;
  onChange: (value: string) => void;
  onEnter: () => void;
  disabled?: boolean;
}> = ({ field, value, rangeFilters, onChange, onEnter, disabled }) => {
  const listId = `dist-${field.key}`;
  const { data } = useDistinctValuesQuery(
    field.dimension,
    { ...rangeFilters, limit: 100 },
    // Don't fire suggestion requests while the feature is unavailable —
    // they would just 404.
    { enabled: !disabled },
  );

  return (
    <label className={styles.filterField}>
      <span className={styles.filterLabel}>{field.label}</span>
      <input
        className={styles.filterInput}
        type="text"
        list={listId}
        placeholder="any"
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            onEnter();
          }
        }}
      />
      <datalist id={listId}>
        {(data?.values ?? []).map((v) => (
          <option key={v} value={v} />
        ))}
      </datalist>
    </label>
  );
};

const CampaignFilterBar: React.FC<Props> = ({
  range,
  attrs,
  rangeFilters,
  onRangeChange,
  onApply,
  onClear,
  disabled = false,
}) => {
  // Draft of all four attr values, committed together by Apply. Re-seeded when
  // the applied attrs change (Clear, back/forward, shared-link load).
  const [draft, setDraft] = useState(attrs);
  useEffect(() => setDraft(attrs), [attrs]);

  const dirty = ATTR_KEYS.some((k) => (draft[k] || '').trim() !== (attrs[k] || ''));
  const hasAny = ATTR_KEYS.some((k) => draft[k] || attrs[k]);

  const apply = () => onApply(draft);

  return (
    <div className={styles.filterBar}>
      <div className={styles.rangeButtons} role="group" aria-label="Time range">
        {RANGES.map((r) => (
          <button
            key={r}
            type="button"
            className={`${styles.rangeButton} ${range === r ? styles.rangeButtonActive : ''}`}
            onClick={() => onRangeChange(r)}
            title={RANGE_LABELS[r]}
            disabled={disabled}
          >
            {r}
          </button>
        ))}
      </div>
      <div className={styles.filterFields}>
        {ATTR_FIELDS.map((field) => (
          <AttrInput
            key={field.key}
            field={field}
            value={draft[field.key]}
            rangeFilters={rangeFilters}
            onChange={(v) => setDraft((d) => ({ ...d, [field.key]: v }))}
            onEnter={apply}
            disabled={disabled}
          />
        ))}
        <div className={styles.filterActions}>
          <button
            type="button"
            className={styles.applyButton}
            onClick={apply}
            disabled={disabled || !dirty}
          >
            Apply
          </button>
          {hasAny && (
            <button
              type="button"
              className={styles.clearButton}
              onClick={onClear}
              disabled={disabled}
            >
              Clear
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CampaignFilterBar;
